/**
 * Noms de permissions alignés sur Spatie (chaîne `name` côté API).
 * Les super-admins passent toujours (voir AuthService.hasPermission).
 */
export const PERM = {
  // — Produits —
  viewProducts: 'view products',
  createProducts: 'create products',
  updateProducts: 'update products',
  deleteProducts: 'delete products',

  // — Catégories —
  viewCategories: 'view categories',
  createCategories: 'create categories',
  updateCategories: 'update categories',
  deleteCategories: 'delete categories',

  // — Taxes —
  viewTaxes: 'view taxes',
  createTaxes: 'create taxes',
  updateTaxes: 'update taxes',
  deleteTaxes: 'delete taxes',

  // — Réductions —
  viewReductions: 'view reductions',
  createReductions: 'create reductions',
  updateReductions: 'update reductions',
  deleteReductions: 'delete reductions',

  // — Commandes —
  viewOrders: 'view orders',
  createOrders: 'create orders',
  updateOrders: 'update orders',
  deleteOrders: 'delete orders',

  // — Utilisateurs & accès —
  viewUsers: 'view users',
  createUsers: 'create users',
  updateUsers: 'update users',
  deleteUsers: 'delete users',
  assignRoles: 'assign roles',
  assignPermissions: 'assign permissions',

  // — Options / Boutiques —
  viewOptions: 'view options',
  viewShops: 'view shops',

  // — Fournisseurs —
  viewSuppliers: 'view suppliers',
  createSuppliers: 'create suppliers',
  updateSuppliers: 'update suppliers',
  deleteSuppliers: 'delete suppliers',

  // — Clients —
  viewClients: 'view clients',
  createClients: 'create clients',
  updateClients: 'update clients',
  deleteClients: 'delete clients',

  // — Entreprises —
  viewCompanies: 'view companies',
  createCompanies: 'create companies',
  updateCompanies: 'update companies',
  deleteCompanies: 'delete companies',

  // — Devis —
  viewQuotes: 'view quotes',
  createQuotes: 'create quotes',
  updateQuotes: 'update quotes',
  deleteQuotes: 'delete quotes',

  // — Factures —
  viewInvoices: 'view invoices',
  createInvoices: 'create invoices',
  updateInvoices: 'update invoices',
  deleteInvoices: 'delete invoices',
  // LOGISTIKI « À arbitrer » : écrire un arbitrage ou l'annuler (lecture = view invoices).
  arbitrerFactures: 'arbitrer factures',

  // — Règlements —
  managePayments: 'manage payments',

  // — Moyens de paiement —
  viewPaymentMethods: 'view payment methods',
  createPaymentMethods: 'create payment methods',
  updatePaymentMethods: 'update payment methods',
  deletePaymentMethods: 'delete payment methods',

  // — Paramètres —
  viewSettings: 'view settings',
  createSettings: 'create settings',
  updateSettings: 'update settings',
  deleteSettings: 'delete settings',

  // — Dépenses —
  viewExpenses: 'view expenses',
  createExpenses: 'create expenses',
  updateExpenses: 'update expenses',
  deleteExpenses: 'delete expenses',

  // — Comptabilité LOGISTIKI (mode expert) —
  voirComptabilite:     'view comptabilite',
  saisirComptabilite:   'saisir comptabilite',
  cloturerComptabilite: 'cloturer comptabilite',
  voirBanque:           'view banque',
  importerBanque:       'importer banque',
  rapprocherBanque:     'rapprocher banque',
  voirExportsCompta:    'view exports compta',
  exporterCompta:       'exporter compta',
  parametresCompta:     'gerer parametres compta',

  // — Rapprochement bancaire —
  viewBankReconciliation: 'view bank reconciliation',
  createBankReconciliation: 'create bank reconciliation',
  updateBankReconciliation: 'update bank reconciliation',
  deleteBankReconciliation: 'delete bank reconciliation',

  // — Abonnements —
  viewSubscriptions: 'view subscriptions',
  createSubscriptions: 'create subscriptions',
  updateSubscriptions: 'update subscriptions',
  deleteSubscriptions: 'delete subscriptions',

  // — Accès modules —
  accessSkedi:      'access.skedi',
  accessAgelii:     'access.agelii',
  accessMoni:       'access.moni',
  accessLogistiki:  'access.logistiki',
  accessPolisi:     'access.polisi',
  // `access.kratisi` et non `access-kratisi` : le point est la convention de
  // toutes les autres apps, et `AppsSeeder` a renommé la permission en base.
  // Tant que cette constante gardait le tiret, elle désignait une permission
  // INEXISTANTE — la tuile KRATISI disparaissait du launcher pour tout le
  // monde, super-admin compris, sans erreur ni trace.
  accessKratisi:    'access.kratisi',
  accessPinaki:     'access.pinaki',
  accessEntoli:     'access.entoli',
  accessArogi:      'access.arogi',

  // ODIGI — module auto-ecole. Meme convention pointee que les autres
  // (`access.odigi`), et garde `web` cote API.
  accessOdigi:      'access.odigi',
  accessPyxidi:     'access.pyxidi',
  viewPyxidi:       'view pyxidi',
  accessKleidi:     'access.kleidi',

  // — KLEIDI (coffre-fort de mots de passe) —
  viewKleidiVault:       'view kleidi vault',
  createKleidiItems:     'create kleidi items',
  updateKleidiItems:     'update kleidi items',
  deleteKleidiItems:     'delete kleidi items',
  shareKleidiItems:      'share kleidi items',
  viewKleidiAdmin:       'view kleidi admin',
  manageKleidiRecovery:  'manage kleidi recovery',
  viewKleidiAudit:       'view kleidi audit',

  // — Arogi (Help Desk) —
  viewArogiTickets:    'view arogi tickets',
  createArogiTickets:  'create arogi tickets',
  updateArogiTickets:  'update arogi tickets',
  deleteArogiTickets:  'delete arogi tickets',
  viewArogiMessages:   'view arogi messages',
  createArogiMessages: 'create arogi messages',
  updateArogiMessages: 'update arogi messages',
  deleteArogiMessages: 'delete arogi messages',
} as const;

export type PermissionName = (typeof PERM)[keyof typeof PERM];
